import { useEffect, useRef, useState } from "react";

import type { FluidCore } from "../lib/fluid";

/**
 * The liquid layer.
 *
 * A fixed, full-viewport canvas sitting between the neural field and the
 * content, running a dye-advection simulation: the cursor pours colour into
 * an invisible liquid and drags ripples through it as it moves.
 *
 * The same contract as the AI Core, because it is the same risk:
 *
 *   - **It loads after first paint.** The simulation is tiny (no geometry,
 *     no mipmaps, quarter-res targets) but it is still a second WebGL
 *     context, and LCP does not wait for decoration.
 *   - **It is `aria-hidden` and `pointer-events: none`.** The canvas never
 *     sees an event; pointer position is read on `window`, so nothing on the
 *     page loses a click or a hover to it.
 *   - **It stops when unlooked-at.** Hidden tab → RAF cancelled, like the
 *     neural field. (Unlike the neural field it does not pause offscreen —
 *     it *is* the screen.)
 *   - **It fails silently.** No WebGL, a context stolen by the AI Core, a
 *     shader that will not compile — the page is complete without it. In
 *     dev the console says what happened, same as AICore.
 *   - **Off on touch, off under reduced motion.** A swipe would otherwise
 *     paint colour behind a scroll gesture, which is novelty at the exact
 *     moment someone is trying to read; and continuous pointer-driven
 *     animation is precisely what the reduced-motion preference exists to
 *     suppress.
 */

export function FluidCursor() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const coreRef = useRef<FluidCore | null>(null);
  const [ready, setReady] = useState(false);

  const [enabled] = useState(
    () =>
      window.matchMedia("(pointer: fine) and (hover: hover)").matches &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    if (!enabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Same object-not-boolean pattern as AICore: the flag is written in the
    // cleanup and read after an await, which a plain boolean cannot survive.
    const lifecycle = { cancelled: false };
    const teardown: (() => void)[] = [];

    void (async () => {
      try {
        const { createFluidCore } = await import("../lib/fluid");
        if (lifecycle.cancelled) return;

        const theme =
          document.documentElement.dataset.theme === "dark" ? "dark" : "light";
        const core = createFluidCore(canvas, {
          reducedMotion: false, // gated by `enabled` above
          theme,
        });
        coreRef.current = core;
        core.resize();
        core.start();
        setReady(true);

        const onResize = () => {
          core.resize();
        };
        window.addEventListener("resize", onResize);
        teardown.push(() => {
          window.removeEventListener("resize", onResize);
        });

        const onVisibility = () => {
          if (document.hidden) core.stop();
          else core.start();
        };
        document.addEventListener("visibilitychange", onVisibility);
        teardown.push(() => {
          document.removeEventListener("visibilitychange", onVisibility);
        });

        // Coalesced events: pointermove can fire at 1000Hz on gaming mice and
        // every one would be a splat. One per frame is plenty for a dye trail.
        let pending = false;
        let lastX = 0;
        let lastY = 0;
        const onMove = (event: PointerEvent) => {
          lastX = event.clientX;
          lastY = event.clientY;
          if (pending) return;
          pending = true;
          requestAnimationFrame(() => {
            pending = false;
            core.splat(lastX, lastY);
          });
        };
        window.addEventListener("pointermove", onMove, { passive: true });
        teardown.push(() => {
          window.removeEventListener("pointermove", onMove);
        });

        const onDown = (event: PointerEvent) => {
          core.burst(event.clientX, event.clientY);
        };
        window.addEventListener("pointerdown", onDown, { passive: true });
        teardown.push(() => {
          window.removeEventListener("pointerdown", onDown);
        });
      } catch (error) {
        // Silent for the visitor, loud for the developer — identical to the
        // AICore contract, for the identical reason.
        if (import.meta.env.DEV) {
          console.warn("[FluidCursor] the fluid layer failed to start:", error);
        }
      }
    })();

    return () => {
      lifecycle.cancelled = true;
      for (const off of teardown) off();
      coreRef.current?.dispose();
      coreRef.current = null;
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`pointer-events-none fixed inset-0 z-[1] h-full w-full transition-opacity duration-1000 ease-out ${
        ready ? "opacity-100" : "opacity-0"
      }`}
    />
  );
}
